import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { SlideType } from '@as-react/common-types';
import { Box, Carousel, CarouselContent, CarouselNext, CarouselPrevious, CarouselSlidePagination, CarouselSlot, ConditionalWrapper, Stack, useBreakpoints, } from '@yonderland/andes-react';
import AutoPlay from 'embla-carousel-autoplay';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { getPercentage } from '../components/carousel/Carousel';
import { I18N_NAMESPACES } from '../constants/i18nConstants';
import { MIN_SCREEN_WIDTH_DESKTOP, MIN_SCREEN_WIDTH_DESKTOP_LARGE, MIN_SCREEN_WIDTH_TABLET, MIN_SCREEN_WIDTH_TABLET_SMALL, } from '../constants/ScreenWidthConstants';
import { SlideCropped } from '../constants/SliderConstants';
import { singleProductTileActions } from '../redux/single-product-tile/actions/SingleProductTileActions.creators';
import { getBoxPropsFromAEMPaddings } from '../util/AEMUtil';
import { SlideRenderer } from './SlideRenderer';
const SliderContainer = ({ paddings, cdnSettings, height, properties, slides, sliderId }) => {
    const { t } = useTranslation([I18N_NAMESPACES.general]);
    const { imageCdnPrefix } = useAemConfig();
    const contentTileTranslations = {
        readMore: t(`${I18N_NAMESPACES.general}:readMore`),
    };
    const dispatch = useDispatch();
    useEffect(() => {
        if (slides.some(slide => slide.type === SlideType.SINGLE_PRODUCT_TILE)) {
            const products = [];
            slides.forEach(slide => {
                if ('initializationProduct' in slide) {
                    products.push(slide.initializationProduct);
                }
            });
            dispatch(singleProductTileActions.addBulkProductsToList(products));
        }
    }, []);
    const { autoplay, displayCroppedSlides, hideOnViewports, infiniteSliding, innerSpacing, showIndicators, slidesToShowDesktop, slidesToScrollDesktop, slidesToShowDesktopLarge, slidesToScrollDesktopLarge, slidesToShowMobile, slidesToScrollMobile, slidesToShowTablet, slidesToScrollTablet, slidesToShowTabletSmall, slidesToScrollTabletSmall, } = properties;
    const slideHeight = height
        ? Object.assign(Object.assign(Object.assign({}, (height.mobile && { xs: `${height.mobile}px` })), (height.tablet && { md: `${height.tablet}px` })), (height.desktop && { lg: `${height.desktop}px` })) : undefined;
    const croppedSmall = displayCroppedSlides ? SlideCropped.SMALL : 0;
    const croppedLarge = displayCroppedSlides ? SlideCropped.LARGE : 0;
    const slideSize = useMemo(() => ({
        xs: getPercentage(slidesToShowMobile, croppedSmall),
        sm: getPercentage(slidesToShowTabletSmall, croppedSmall),
        md: getPercentage(slidesToShowTablet, croppedLarge),
        lg: getPercentage(slidesToShowDesktop, croppedLarge),
        xl: getPercentage(slidesToShowDesktopLarge, croppedLarge),
    }), [
        slidesToShowMobile,
        slidesToShowTabletSmall,
        slidesToShowTablet,
        slidesToShowDesktop,
        slidesToShowDesktopLarge,
        croppedSmall,
        croppedLarge,
    ]);
    const emblaOpts = useMemo(() => ({
        loop: infiniteSliding,
        slidesToScroll: slidesToScrollMobile,
        breakpoints: {
            [`(min-width: ${MIN_SCREEN_WIDTH_TABLET_SMALL}px)`]: { slidesToScroll: slidesToScrollTabletSmall },
            [`(min-width: ${MIN_SCREEN_WIDTH_TABLET}px)`]: { slidesToScroll: slidesToScrollTablet },
            [`(min-width: ${MIN_SCREEN_WIDTH_DESKTOP}px)`]: { slidesToScroll: slidesToScrollDesktop },
            [`(min-width: ${MIN_SCREEN_WIDTH_DESKTOP_LARGE}px)`]: { slidesToScroll: slidesToScrollDesktopLarge },
        },
    }), [
        infiniteSliding,
        slidesToScrollMobile,
        slidesToScrollTabletSmall,
        slidesToScrollTablet,
        slidesToScrollDesktop,
        slidesToScrollDesktopLarge,
    ]);
    const { active } = useBreakpoints();
    const slidesToShow = useMemo(() => {
        if (active === 'xs')
            return slidesToShowMobile;
        if (active === 'sm')
            return slidesToShowTabletSmall;
        if (active === 'md')
            return slidesToShowTablet;
        if (active === 'lg')
            return slidesToShowDesktop;
        if (active === 'xl')
            return slidesToShowDesktopLarge;
        return slidesToShowDesktopLarge;
    }, [
        active,
        slidesToShowMobile,
        slidesToShowTabletSmall,
        slidesToShowTablet,
        slidesToShowDesktop,
        slidesToShowDesktopLarge,
    ]);
    const hasContentTile = useMemo(() => slides.some(slide => slide.type === SlideType.CONTENT_TILE), [slides]);
    // Add extra slides to allow infinite sliding when the number of slides is less than or equal to the number of slides to show + 1
    const extraSlides = infiniteSliding && slides.length <= slidesToShow + 1 ? [...slides, ...slides] : slides;
    const showPagination = showIndicators && !infiniteSliding;
    const carousel = (_jsx(Carousel, { testId: sliderId, slideSize: slideSize, slideHeight: slideHeight, plugins: [
            AutoPlay(autoplay
                ? {
                    active: true,
                    delay: autoplay.delay,
                    stopOnLastSnap: autoplay.stopOnLastSlide,
                    stopOnMouseEnter: true,
                    stopOnInteraction: false,
                }
                : { active: false }),
        ], opts: emblaOpts, gap: innerSpacing ? '4' : '0', children: _jsxs(Stack, { direction: "col", gap: hasContentTile ? '2' : '4', children: [_jsx(CarouselContent, { children: extraSlides.map((slide, index) => (_jsx(CarouselSlot, { children: _jsx(ConditionalWrapper, { condition: hasContentTile, wrapper: children => _jsx(Box, { paddingBlock: "4", children: children }), children: _jsx(SlideRenderer, { slide: slide, cdnSettings: cdnSettings, imageCdnPrefix: imageCdnPrefix, contentTileTranslations: contentTileTranslations, slidesToShowDesktop: slidesToShowDesktop }) }) }, `slide_component_${index}`))) }), _jsxs(Stack, { wrap: "nowrap", justify: showPagination ? 'between' : 'end', align: "center", gap: "1", children: [showPagination && _jsx(CarouselSlidePagination, {}), _jsxs(Stack, { wrap: "nowrap", gap: "1", children: [_jsx(CarouselPrevious, {}), _jsx(CarouselNext, {})] })] })] }) }));
    return (_jsx(Box, Object.assign({}, getBoxPropsFromAEMPaddings(paddings), { children: hideOnViewports ? _jsx(Box, { display: { xs: 'none', md: 'block' }, children: carousel }) : carousel })));
};
export default SliderContainer;
